// PlateDesigner — now backed by the unified `UAEPlate` component (12 official designs).
// Provides the same public API used by CreateStore.js:
//   <PlateDesigner onDataChange={fn} initialData={obj} />
// and preserves the `emiratePlateConfigs` + `UAEPlate` re-exports (used by stores/index.js).
import { useMemo, useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useTheme } from '../../context/ThemeContext';
import UAEPlate, { hasPlateDesign } from '../plates/UAEPlate';

// Canonical emirate → UI label + supported category codes for the picker
const emiratePlateConfigs = {
  dubai: {
    name: { ar: 'دبي', en: 'Dubai' },
    canonical: 'dubai',
    categories: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').concat(['AA']).map(l => ({ id: l, label: l })),
  },
  abu_dhabi: {
    name: { ar: 'أبوظبي', en: 'Abu Dhabi' },
    canonical: 'abu_dhabi',
    categories: ['1','2','3','4','5','6','7','8','9','10','11','12','13','14','15','16','17','50'].map(n => ({ id: n, label: n })),
  },
  sharjah: {
    name: { ar: 'الشارقة', en: 'Sharjah' },
    canonical: 'sharjah',
    categories: ['1','2','3'].map(n => ({ id: n, label: n })),
  },
  ajman: {
    name: { ar: 'عجمان', en: 'Ajman' },
    canonical: 'ajman',
    categories: ['A','B','C','D','E'].map(l => ({ id: l, label: l })),
  },
  ras_al_khaimah: {
    name: { ar: 'رأس الخيمة', en: 'Ras Al Khaimah' },
    canonical: 'ras_al_khaimah',
    categories: ['A','B','C','D','K','M','N','O','P','R','S','V','W','Y'].map(l => ({ id: l, label: l })),
  },
  fujairah: {
    name: { ar: 'الفجيرة', en: 'Fujairah' },
    canonical: 'fujairah',
    categories: ['A','B','C','D','E','K'].map(l => ({ id: l, label: l })),
  },
  umm_al_quwain: {
    name: { ar: 'أم القيوين', en: 'Umm Al Quwain' },
    canonical: 'umm_al_quwain',
    categories: ['A','B','C','D'].map(l => ({ id: l, label: l })),
  },
};

// Support legacy inbound keys ('abudhabi', 'rak', 'uaq')
const EMIRATE_ALIAS = {
  abudhabi: 'abu_dhabi',
  rak: 'ras_al_khaimah',
  uaq: 'umm_al_quwain',
};
const normalize = (e) => EMIRATE_ALIAS[e] || e;

// Plate types — mapped to what the unified component understands
const PLATE_TYPES = [
  { id: 'private', ar: 'خصوصي', en: 'Private' },
  { id: 'motorcycle', ar: 'دراجة نارية', en: 'Motorcycle' },
  { id: 'classic', ar: 'كلاسيك', en: 'Classic' },
];

export default function PlateDesigner({ onDataChange, initialData = {} }) {
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();

  const [emirate, setEmirate] = useState(normalize(initialData.emirate || 'dubai'));
  const [plateType, setPlateType] = useState(initialData.plateType || 'private');
  const [category, setCategory] = useState(initialData.category || ''); // this is the CODE
  const [number, setNumber] = useState(initialData.number || '');
  const [price, setPrice] = useState(initialData.price || '');
  const [description, setDescription] = useState(initialData.description || '');

  const plateConfig = emiratePlateConfigs[emirate];
  const supportedTypes = useMemo(
    () => PLATE_TYPES.filter(t => hasPlateDesign(emirate, t.id)),
    [emirate]
  );

  const emit = (patch) => {
    const merged = { emirate, plateType, category, number, price, description, ...patch };
    onDataChange?.(merged);
  };

  const handleEmirate = (key) => {
    const normal = normalize(key);
    setEmirate(normal);
    // Reset plateType to first supported one if current is not supported
    const supported = PLATE_TYPES.filter(t => hasPlateDesign(normal, t.id));
    const newType = supported.find(t => t.id === plateType)?.id || supported[0]?.id || 'private';
    setPlateType(newType);
    setCategory('');
    emit({ emirate: normal, plateType: newType, category: '' });
  };

  const handleType = (id) => {
    setPlateType(id);
    if (id === 'classic') setCategory(''); // classic has no code
    emit({ plateType: id, category: id === 'classic' ? '' : category });
  };

  const handleCategory = (id) => {
    setCategory(id);
    emit({ category: id });
  };

  const handleNumber = (v) => {
    const cleaned = v.replace(/\D/g, '').slice(0, 5);
    setNumber(cleaned);
    emit({ number: cleaned });
  };

  const handlePrice = (v) => { setPrice(v); emit({ price: v }); };
  const handleDesc = (v) => { setDescription(v); emit({ description: v }); };

  const showCode = plateType !== 'classic';

  return (
    <div className="space-y-6">
      {/* Live Plate Preview */}
      <div className={`rounded-xl p-6 border ${isDarkMode ? 'bg-[#1E293B] border-[#334155]' : 'bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200'}`}>
        <h3 className={`font-bold text-lg mb-4 text-center ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          {language === 'ar' ? 'معاينة اللوحة' : 'Plate Preview'}
        </h3>

        <div className="flex justify-center">
          {hasPlateDesign(emirate, plateType) ? (
            <UAEPlate
              emirate={emirate}
              category={plateType}
              code={category || ''}
              number={number || '12345'}
              width={plateType === 'motorcycle' ? 300 : 420}
            />
          ) : (
            <div
              className="flex items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 text-gray-500 text-sm"
              style={{ width: 420, height: 96 }}
              data-testid="plate-designer-unsupported"
            >
              {language === 'ar'
                ? 'تصميم اللوحة غير متوفر لهذه الإمارة/الفئة'
                : 'Plate design not available for this emirate/category'}
            </div>
          )}
        </div>
      </div>

      {/* Emirate Selection */}
      <div className={`rounded-xl p-6 border ${isDarkMode ? 'bg-[#1E293B] border-[#334155]' : 'bg-white border-gray-200'}`}>
        <label className={`block font-medium mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          {language === 'ar' ? 'الإمارة *' : 'Emirate *'}
        </label>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {Object.entries(emiratePlateConfigs).map(([key, config]) => (
            <button
              key={key}
              type="button"
              onClick={() => handleEmirate(key)}
              className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                emirate === key
                  ? 'border-[#1E9BD9] bg-[#1E9BD9]/10 text-[#1E9BD9]'
                  : isDarkMode
                    ? 'border-[#475569] hover:border-[#1E9BD9]/50 text-gray-300'
                    : 'border-gray-200 hover:border-[#1E9BD9]/50 text-gray-700'
              }`}
              data-testid={`plate-emirate-${key}`}
            >
              {language === 'ar' ? config.name.ar : config.name.en}
            </button>
          ))}
        </div>
      </div>

      {/* Plate Type (private / motorcycle / classic) */}
      {supportedTypes.length > 0 && (
        <div className={`rounded-xl p-6 border ${isDarkMode ? 'bg-[#1E293B] border-[#334155]' : 'bg-white border-gray-200'}`}>
          <label className={`block font-medium mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {language === 'ar' ? 'نوع اللوحة *' : 'Plate Type *'}
          </label>
          <div className="flex flex-wrap gap-2">
            {supportedTypes.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => handleType(t.id)}
                className={`px-4 h-11 rounded-lg border-2 font-semibold text-sm transition-all ${
                  plateType === t.id
                    ? 'border-[#1E9BD9] bg-[#1E9BD9] text-white'
                    : isDarkMode
                      ? 'border-[#475569] hover:border-[#1E9BD9]/50 text-gray-300'
                      : 'border-gray-200 hover:border-[#1E9BD9]/50 text-gray-700'
                }`}
                data-testid={`plate-type-${t.id}`}
              >
                {language === 'ar' ? t.ar : t.en}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Code / Category Selection (skip for classic) */}
      {showCode && (
        <div className={`rounded-xl p-6 border ${isDarkMode ? 'bg-[#1E293B] border-[#334155]' : 'bg-white border-gray-200'}`}>
          <label className={`block font-medium mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {language === 'ar' ? 'الكود *' : 'Code *'}
          </label>

          <div className="flex flex-wrap gap-2">
            {plateConfig?.categories.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => handleCategory(cat.id)}
                className={`w-12 h-12 rounded-lg border-2 font-bold transition-all ${
                  category === cat.id
                    ? 'border-[#1E9BD9] bg-[#1E9BD9] text-white'
                    : isDarkMode
                      ? 'border-[#475569] hover:border-[#1E9BD9]/50 text-gray-300'
                      : 'border-gray-200 hover:border-[#1E9BD9]/50 text-gray-700'
                }`}
                data-testid={`plate-code-${cat.id}`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Plate Number */}
      <div className={`rounded-xl p-6 border ${isDarkMode ? 'bg-[#1E293B] border-[#334155]' : 'bg-white border-gray-200'}`}>
        <label className={`block font-medium mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          {language === 'ar' ? 'رقم اللوحة *' : 'Plate Number *'}
        </label>

        <input
          type="text"
          value={number}
          onChange={(e) => handleNumber(e.target.value)}
          placeholder="12345"
          maxLength={5}
          className={`w-full h-14 rounded-xl border-2 px-4 text-2xl font-bold text-center tracking-widest ${
            isDarkMode
              ? 'bg-[#0F172A] border-[#334155] text-white'
              : 'bg-white border-gray-200 text-gray-900'
          }`}
          data-testid="plate-number-input"
        />

        <p className={`text-sm mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {language === 'ar' ? 'أدخل من 1 إلى 5 أرقام' : 'Enter 1 to 5 digits'}
        </p>
      </div>

      {/* Price */}
      <div className={`rounded-xl p-6 border ${isDarkMode ? 'bg-[#1E293B] border-[#334155]' : 'bg-white border-gray-200'}`}>
        <label className={`block font-medium mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          {language === 'ar' ? 'السعر (درهم) *' : 'Price (AED) *'}
        </label>

        <input
          type="number"
          value={price}
          onChange={(e) => handlePrice(e.target.value)}
          placeholder="50000"
          className={`w-full h-12 rounded-xl border px-4 text-lg ${
            isDarkMode
              ? 'bg-[#0F172A] border-[#334155] text-white'
              : 'bg-white border-gray-200 text-gray-900'
          }`}
          data-testid="plate-price-input"
        />
      </div>

      {/* Description */}
      <div className={`rounded-xl p-6 border ${isDarkMode ? 'bg-[#1E293B] border-[#334155]' : 'bg-white border-gray-200'}`}>
        <label className={`block font-medium mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          {language === 'ar' ? 'وصف إضافي' : 'Additional Description'}
        </label>

        <textarea
          value={description}
          onChange={(e) => handleDesc(e.target.value)}
          placeholder={language === 'ar' ? 'معلومات إضافية عن اللوحة...' : 'Additional information about the plate...'}
          rows={3}
          className={`w-full rounded-xl border px-4 py-3 text-sm ${
            isDarkMode
              ? 'bg-[#0F172A] border-[#334155] text-white'
              : 'bg-white border-gray-200 text-gray-900'
          }`}
          data-testid="plate-description-textarea"
        />
      </div>
    </div>
  );
}

// Preserve exports for stores/index.js (re-exports these three symbols)
export { emiratePlateConfigs, UAEPlate };
